import React from "react";

// ---------- Shared card shell ----------
export const AnalyticsCard = ({
  title,
  subtitle,
  isLoading,
  children,
  className = "",
}: {
  title: string;
  subtitle?: string;
  isLoading?: boolean;
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={`bg-white rounded-lg border border-gray-200 p-4 ${className}`}>
    <h4 className="text-sm font-semibold">
      {title}
      {subtitle && (
        <span className="ml-1 font-normal text-gray-500">({subtitle})</span>
      )}
    </h4>
    <div className="mt-3">
      {isLoading ? (
        <div className="text-sm text-gray-400 py-10 text-center">Loading...</div>
      ) : (
        children
      )}
    </div>
  </div>
);

// ---------- Donut (pure SVG, no chart lib needed) ----------
export const DonutChart = ({
  segments,
  size = 130,
}: {
  segments: { label: string; value: number; color: string }[];
  size?: number;
}) => {
  const r = 48;
  const c = 2 * Math.PI * r;
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox="0 0 120 120">
      <circle cx="60" cy="60" r={r} fill="none" stroke="#f3f4f6" strokeWidth="18" />
      {segments.map((s) => {
        const len = (s.value / total) * c;
        const el = (
          <circle
            key={s.label}
            cx="60"
            cy="60"
            r={r}
            fill="none"
            stroke={s.color}
            strokeWidth="18"
            strokeDasharray={`${len} ${c - len}`}
            strokeDashoffset={-offset}
            transform="rotate(-90 60 60)"
          />
        );
        offset += len;
        return el;
      })}
    </svg>
  );
};

// ---------- Horizontal bar list (district / block / top-bottom) ----------
export const BarList = ({
  rows,
  name,
  value,
  color = "#16a34a",
  emptyText = "No data available",
}: {
  rows: any;
  name:any,
  value:any,
  color?: string;
  emptyText?: string;
}) =>
  rows.length === 0 ? (
    <div className="text-sm text-gray-400 py-6 text-center">{emptyText}</div>
  ) : (
    <div className="flex flex-col gap-2">
      {rows.map((r:any) => (
        <div key={r.name} className="flex items-center gap-3 text-xs">
          <span className="w-24 truncate text-gray-700">{r[name]}</span>
          <div className="flex-1 h-2 rounded-full bg-gray-100">
            <div
              className="h-2 rounded-full"
              style={{ width: `${Math.min(r.value, 100)}%`, background: color }}
            />
          </div>
          <span className="w-10 text-right font-semibold">{r[value]}%</span>
        </div>
      ))}
    </div>
  );

// ---------- Pre vs Post columns ----------
export const PrePostChart = ({
  pre,
  post,
  lean,
}: {
  pre: number;
  post: number;
  lean: number;
}) => (
  <div>
    <div className="flex items-end justify-center gap-16 h-44 border-b border-gray-200">
      {[
        { label: "Pre Test", v: pre, color: "#f59e0b" },
        { label: "Post Test", v: post, color: "#16a34a" },
        { label: "Post Test", v: lean, color: "#ef4444" },
      ].map((b) => (
        <div
          key={b.label}
          className="flex flex-col items-center justify-end h-full"
        >
          <div
            className="w-16 rounded-t text-white text-xs font-semibold flex items-start justify-center pt-1"
            style={{ height: `${Math.max(b.v, 2)}%`, background: b.color }}
          >
            {b.v}%
          </div>
        </div>
      ))}
    </div>
    <div className="flex justify-center gap-16 mt-1 text-xs text-gray-600">
      <span className="w-16 flex justify-center">Pre Test</span>
      <span className="w-16 flex justify-center">Post Test</span>
      <span className="w-16 flex justify-center">Lean Gain</span>
    </div>
  </div>
);

// ---------- Line chart (submission tracker) ----------
export const LineTrend = ({
  points,
}: {
  points: { label: string; value: number }[];
}) => {
  if (points.length < 2)
    return <div className="text-sm text-gray-400 py-10 text-center">No submissions yet</div>;
  const W = 400, H = 140, pad = 8;
  const max = Math.max(...points.map((p) => p.value), 1);
  const xy = points.map((p, i) => [
    pad + (i / (points.length - 1)) * (W - pad * 2),
    H - pad - (p.value / max) * (H - pad * 2),
  ]);
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-36">
        <polyline
          fill="none"
          stroke="#7c6ee6"
          strokeWidth="2"
          points={xy.map((p) => p.join(",")).join(" ")}
        />
      </svg>
      <div className="flex justify-between text-[10px] text-gray-500">
        <span>{points[0].label}</span>
        <span>{points[points.length - 1].label}</span>
      </div>
    </div>
  );
};
